import React from 'react';
import { SideSheet, SideSheetProps, Spinner, Flex } from 'pounceV2';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { useHistory } from 'react-router';
import { Rule, UpdateRuleAndFilterInput } from 'Generated/schema';

import { extractErrorMessage } from 'Helpers/utils';
import {
  ExtractedFilterCondition,
  ExtractedFilterConditions,
} from 'Components/forms/DetectionFieldFiltersForm/constants';
import {
  FiltersWithJoins,
  formatDSFAsFilters,
  formatFiltersAsDSF,
} from 'Pages/Build/Detections/utils';
import SaveChangesPromptV3 from 'Components/modals/SaveChangesPromptV3';
import {
  useCreatePullRequest,
  useGetRule,
  useUpdateRule,
} from 'Pages/Build/Detections/Rules/graphql/rule.generated';
import useIsCreatePROnSaveEnabled from 'Hooks/useIsCreatePROnSaveEnabled';
import RuleFilterSidesheetFormV3 from './RuleFilterSidesheetFormV3';

export type RuleFilterSidesheetValues = UpdateRuleAndFilterInput & {
  filters?: ExtractedFilterConditions;
  inlineFiltersGUI?: FiltersWithJoins;
  simpleDetectionBodyGUI?: FiltersWithJoins;
  alertEventValues?: Record<string, any>;
  addTest: boolean;
};

export type RuleFilterSidesheetNewFilterEntry = Omit<ExtractedFilterCondition, 'operator'> & {
  operator?: string;
};

export type RuleFilterSidesheetProps = {
  cacheSessionId?: string;
  /** Rule ID - required when rendered via SidesheetManager, optional when always mounted in RuleAlertDetails */
  ruleId?: string;
  newFilter?: RuleFilterSidesheetNewFilterEntry | RuleFilterSidesheetNewFilterEntry[];
  alertEventValues?: Record<string, any>;
  /** Callback to receive the Formik instance (used when always mounted in RuleAlertDetails) */
  onFormikReady?: (formik: FormikProps<RuleFilterSidesheetValues>) => void;
} & SideSheetProps;

const RuleFilterSidesheetV3: React.FC<RuleFilterSidesheetProps> = ({
  ruleId,
  newFilter,
  open,
  onClose,
  alertEventValues,
  onFormikReady,
}) => {
  const history = useHistory();
  const [isSuccess, setIsSuccess] = React.useState(false);
  const {
    data: { rule = {} as Rule } = {},
    error: fetchRuleError,
    loading: isFetchingRule,
  } = useGetRule({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: ruleId
      ? {
          input: {
            id: ruleId,
          },
        }
      : undefined,
    skip: !ruleId,
  });

  const onRuleSave = React.useCallback(() => {
    setIsSuccess(true);
  }, [setIsSuccess]);

  const [updateRule, { error: updateRuleError, reset: resetUpdateRule }] = useUpdateRule({
    onCompleted: onRuleSave,
    onError: () => null,
  });

  const { isEnabled: isCreatePROnSaveEnabled } = useIsCreatePROnSaveEnabled();
  const [
    createPullRequest,
    { data: createPullRequestData, error: createPullRequestError, reset: resetCreatePullRequest },
  ] = useCreatePullRequest();

  const error = React.useMemo(
    () => extractErrorMessage(fetchRuleError || updateRuleError || createPullRequestError),
    [fetchRuleError, updateRuleError, createPullRequestError],
  );

  // TODO: Centralize this. Use creation wizard's createInitialValues util?
  // https://app.asana.com/0/1200908948600035/1205467344979477/f
  const initialValues = React.useMemo(
    () => ({
      id: rule.id,
      body: rule.body,
      dedupPeriodMinutes: rule.dedupPeriodMinutes,
      threshold: rule.threshold,
      description: rule.description,
      displayName: rule.displayName,
      enabled: rule.enabled,
      managed: rule.managed,
      parentId: rule.parentId,
      logTypes: rule.logTypes,
      scheduledQueries: rule.scheduledQueries,
      outputIds: rule.outputIds,
      reference: rule.reference,
      reports: rule.reports,
      runbook: rule.runbook,
      severity: rule.severity,
      summaryAttributes: rule.summaryAttributes,
      tags: rule.tags,
      analysisType: rule.analysisType,
      tests: rule.tests, // No need to deserialize since the user can't edit existing ones
      alertEventValues,
      isSimpleDetection: rule.isSimpleDetection,
      simpleDetectionBody: rule.simpleDetectionBody,
      simpleDetectionBodyGUI: (rule.simpleDetectionBody
        ? formatDSFAsFilters(rule.simpleDetectionBody)
        : {
            conjunction: 'and',
            values: [],
          }) as FiltersWithJoins,
      inlineFilters: rule.inlineFilters,
      inlineFiltersGUI: (rule.inlineFilters
        ? formatDSFAsFilters(rule.inlineFilters)
        : {
            conjunction: 'and',
            values: [],
          }) as FiltersWithJoins,
      dynamicGroupBy: '',
      dynamicAlertTitle: rule.dynamicAlertTitle || '',
      dynamicAlertContext: '',
      dynamicSeverities: '',
      createAlert: rule.createAlert,
      addTest: false,
    }),
    [rule, alertEventValues],
  );

  const reset = React.useCallback(() => {
    if (isCreatePROnSaveEnabled) {
      resetCreatePullRequest();
    } else {
      resetUpdateRule();
    }

    setIsSuccess(false);
  }, [resetUpdateRule, resetCreatePullRequest, isCreatePROnSaveEnabled]);

  const handleSubmit = React.useCallback(
    (
      values: RuleFilterSidesheetValues,
      formikHelpers: FormikHelpers<RuleFilterSidesheetValues>,
    ) => {
      const {
        inlineFiltersGUI,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        simpleDetectionBodyGUI,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        alertEventValues: event,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        addTest,
        ...ruleValues
      } = values;
      setIsSuccess(false);

      const updateRuleInput = {
        ...ruleValues,
        inlineFilters: formatFiltersAsDSF(inlineFiltersGUI),
      };

      if (isCreatePROnSaveEnabled) {
        return createPullRequest({
          variables: {
            input: {
              ruleEditInput: updateRuleInput,
            },
          },
          onCompleted: () => {
            // Reset the form with the new values to ensure submit button stays disabled
            formikHelpers.resetForm({ values });
            onRuleSave();
          },
          onError: () => null,
        });
      }

      return updateRule({
        variables: {
          input: updateRuleInput,
        },
      });
    },
    [createPullRequest, isCreatePROnSaveEnabled, onRuleSave, updateRule],
  );

  const handleFormikRef = React.useCallback(
    (formik: FormikProps<RuleFilterSidesheetValues>) => {
      // Expose Formik instance to parent via callback
      if (onFormikReady && formik) {
        onFormikReady(formik);
      }
    },
    [onFormikReady],
  );

  React.useEffect(() => {
    if (!open && isSuccess) {
      reset();
    }
  }, [open, isSuccess, reset]);
  React.useEffect(() => reset, [reset]);
  React.useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname]);

  // If ruleId is not provided, we can't render (should not happen in normal flow)
  if (!ruleId) {
    return null;
  }

  return isFetchingRule ? (
    <SideSheet open={open} onClose={onClose} aria-label="Quick Filter Sidesheet">
      <Flex align="center" justify="center" minWidth={900} height="100%">
        <Spinner />
      </Flex>
    </SideSheet>
  ) : (
    <Formik<RuleFilterSidesheetValues>
      innerRef={handleFormikRef}
      onSubmit={handleSubmit}
      initialValues={initialValues as RuleFilterSidesheetValues}
      enableReinitialize
    >
      {props => (
        <>
          {/* SaveChangesPrompt must be outside SideSheet to intercept navigation even when sidesheet is closed */}
          <SaveChangesPromptV3
            title="Discard Changes"
            subtitle="Would you like to discard the current changes to this Detection?"
            cancelBtnText="Back to Edit"
            confirmBtnText="Discard Changes"
            when={props.dirty && !props.isSubmitting}
          />
          <SideSheet open={open} onClose={onClose} aria-label="Quick Filter Sidesheet">
            <RuleFilterSidesheetFormV3
              newFilter={newFilter}
              onClose={() => onClose()}
              onDiscard={reset}
              isSuccess={isSuccess}
              error={error}
              additionalSuccessMessageData={{
                prUrl: createPullRequestData?.createPullRequestForRuleEdit?.url,
              }}
              {...props}
            />
          </SideSheet>
        </>
      )}
    </Formik>
  );
};

export default RuleFilterSidesheetV3;
